import Matter from "matter-js";
import * as C from "../../config/constants.js";
import { EventBus } from "../../core/event-bus.js";
import { DROP_PARTICLE_COUNT, DROP_SPLASH_MS, MERGE_PARTICLE_MS } from "./constants.js";
import { createFlaskMergeBurstParticles } from "../../run/merge-particles.js";
import { clamp } from "./math.js";
import { getFlaskPanelHeight } from "./geometry.js";
import { onFlaskBodySpawned } from "./overflow-state.js";
import { requestContainmentBurst } from "./containment.js";

export function createDropParticles(x, y, count, tierColor, bounds) {
  var particles = [];
  var baseColor = tierColor || "#94a3b8";
  var h = getFlaskPanelHeight(bounds);
  var speedBase = h * (C.FLASK_DROP_SPLASH_SPEED_FRAC != null
    ? Number(C.FLASK_DROP_SPLASH_SPEED_FRAC) : 0.0075);
  var radiusBase = h * (C.FLASK_DROP_SPLASH_RADIUS_FRAC != null
    ? Number(C.FLASK_DROP_SPLASH_RADIUS_FRAC) : 0.01);
  if (!isFinite(speedBase) || speedBase <= 0) speedBase = 3;
  if (!isFinite(radiusBase) || radiusBase <= 0) radiusBase = 4;
  for (var i = 0; i < count; i++) {
    var angle = (i / count) * Math.PI * 2 + Math.random() * 0.5;
    var speed = speedBase * (0.67 + Math.random() * 0.67);
    particles.push({
      x: x,
      y: y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - speedBase * 0.67,
      radius: radiusBase * (0.75 + Math.random() * 0.5),
      alpha: 0.9,
      color: baseColor,
    });
  }
  return particles;
}

export function spawnMergeEffect(system, x, y, tier, opts) {
  opts = opts || {};
  var tierColors = C.MERGE_PICKUP_COLORS || [];
  var tierColor = tierColors[tier] || "#94a3b8";
  var now = Date.now();
  var bodyRadius = opts.bodyRadius;
  if (bodyRadius == null && system.bodyRadiusByTier && system.bodyRadiusByTier[tier] != null) {
    bodyRadius = system.bodyRadiusByTier[tier];
  }
  system.mergeParticles = createFlaskMergeBurstParticles(
    x,
    y,
    tierColor,
    bodyRadius,
    C.RUN_CONFIG,
    { seed: now }
  );
  system.mergeParticlesStart = now;
  system.mergeParticlesUntil = now + MERGE_PARTICLE_MS;
}

function createEllipseVertices(radiusX, radiusY, segments, tiltAngle) {
  var verts = [];
  var cosT = tiltAngle ? Math.cos(tiltAngle) : 1;
  var sinT = tiltAngle ? Math.sin(tiltAngle) : 0;
  for (var i = 0; i < segments; i++) {
    var angle = (i / segments) * Math.PI * 2;
    var x = radiusX * Math.cos(angle);
    var y = radiusY * Math.sin(angle);
    if (tiltAngle) {
      verts.push({ x: x * cosT - y * sinT, y: x * sinT + y * cosT });
    } else {
      verts.push({ x: x, y: y });
    }
  }
  return verts;
}

function mergeAnimalMatterOptions() {
  var slop = C.FLASK_MERGE_BODY_SLOP != null
    ? Number(C.FLASK_MERGE_BODY_SLOP) : 0.02;
  return {
    restitution: C.FLASK_MERGE_BODY_RESTITUTION != null
      ? Number(C.FLASK_MERGE_BODY_RESTITUTION) : 0.2,
    friction: C.FLASK_MERGE_BODY_FRICTION != null
      ? Number(C.FLASK_MERGE_BODY_FRICTION) : 0.4,
    frictionStatic: C.FLASK_MERGE_BODY_FRICTION_STATIC != null
      ? Number(C.FLASK_MERGE_BODY_FRICTION_STATIC) : 1,
    frictionAir: C.FLASK_MERGE_BODY_FRICTION_AIR != null
      ? Number(C.FLASK_MERGE_BODY_FRICTION_AIR) : 0.01,
    slop: isFinite(slop) && slop >= 0 ? slop : 0.02,
    density: 0.001,
  };
}

/** Jar walls/lid: restitution 0, matched static friction. Matter setStatic forces kinetic friction to 1. */
export function mergeFlaskStaticMatterOptions() {
  var body = mergeAnimalMatterOptions();
  return {
    isStatic: true,
    restitution: 0,
    friction: body.friction,
    frictionStatic: body.frictionStatic,
  };
}

export function createBodyForSystem(system, tier, x, y, opts) {
  opts = opts || {};
  var Bodies = Matter.Bodies;
  var sizeTier = opts.physicsSizeTier != null ? opts.physicsSizeTier : tier;
  var avgRadius = system.bodyRadiusByTier[sizeTier] != null ? system.bodyRadiusByTier[sizeTier] : 10;
  var chain = system.evolutionChain || [];
  var cfg = chain[tier] || {};
  var aspect =
    cfg.ellipseAspect != null
      ? cfg.ellipseAspect
      : (C.MERGE_BODY_ELLIPSE_ASPECT_DEFAULT != null ? C.MERGE_BODY_ELLIPSE_ASPECT_DEFAULT : 1.0);
  var ellipseAngle =
    cfg.ellipseAngle != null
      ? cfg.ellipseAngle
      : (C.MERGE_BODY_ELLIPSE_ANGLE_DEFAULT != null ? C.MERGE_BODY_ELLIPSE_ANGLE_DEFAULT : 0);
  var radiusX;
  var radiusY;
  if (Math.abs(aspect - 1) < 0.01) {
    radiusX = radiusY = avgRadius;
  } else {
    radiusX = (2 * avgRadius * aspect) / (1 + aspect);
    radiusY = (2 * avgRadius) / (1 + aspect);
  }
  var body;
  var matterOpts = mergeAnimalMatterOptions();
  if (Math.abs(aspect - 1) < 0.01) {
    body = Bodies.circle(x, y, avgRadius, matterOpts);
  } else {
    var verts = createEllipseVertices(radiusX, radiusY, 24, ellipseAngle);
    body = Bodies.fromVertices(x, y, [verts], matterOpts);
    if (!body) {
      body = Bodies.circle(x, y, avgRadius, matterOpts);
    }
  }
  if (!body) return null;
  return {
    tier: tier,
    physicsSizeTier: sizeTier,
    minimized: !!opts.minimized,
    body: body,
    bodyId: body.id,
    radiusX: radiusX,
    radiusY: radiusY,
  };
}

/** Mark a flask body as in-cargo for mouth containment (default unless entering from above). */
export function markFlaskCargoMeta(meta, opts) {
  if (!meta) return;
  meta._flaskEntered = !(opts && opts.allowMouthEntry);
}

/** Released drop: extra first-hit bounce, then {@link finishFlaskDropLanding} restores settled restitution. */
export function applyFlaskDropLanding(meta, bounds) {
  if (!meta || !meta.body) return;
  meta._dropLanding = true;
  var bounce = C.FLASK_DROP_LANDING_RESTITUTION != null
    ? Number(C.FLASK_DROP_LANDING_RESTITUTION)
    : 0.52;
  if (!isFinite(bounce) || bounce < 0) bounce = 0.52;
  meta.body.restitution = bounce;
  var h = getFlaskPanelHeight(bounds);
  var frac = C.FLASK_DROP_LANDING_VY_FRAC != null
    ? Number(C.FLASK_DROP_LANDING_VY_FRAC)
    : 0;
  var dropVy = h * frac;
  if (isFinite(dropVy) && dropVy > 0) {
    var vx = meta.body.velocity ? meta.body.velocity.x : 0;
    var vy = meta.body.velocity ? meta.body.velocity.y : 0;
    Matter.Body.setVelocity(meta.body, { x: vx, y: Math.max(vy, dropVy) });
  }
}

export function finishFlaskDropLanding(meta) {
  if (!meta || !meta._dropLanding || !meta.body) return;
  meta._dropLanding = false;
  var settled = C.FLASK_MERGE_BODY_RESTITUTION != null
    ? Number(C.FLASK_MERGE_BODY_RESTITUTION)
    : 0.2;
  meta.body.restitution = isFinite(settled) && settled >= 0 ? settled : 0.2;
}

export function dropIntoFlaskForSystem(system, tier, screenX, fallPanelWidth, fallMouthCenterX, fallMouthWidth) {
  system.ensureEngine();
  var World = Matter.World;
  var centerX = (system.bounds.left + system.bounds.right) / 2;
  var fallbackMouthCenterX = fallMouthCenterX != null ? fallMouthCenterX : fallPanelWidth / 2;
  var radius = system.bodyRadiusByTier[tier] != null ? system.bodyRadiusByTier[tier] : 10;
  var Wt = system._flaskGeometry && system._flaskGeometry.W != null ? system._flaskGeometry.W : 0;
  var bL = system.bounds.left + Wt + radius;
  var bR = system.bounds.right - Wt - radius;
  var spawnMinX = system._flaskInterior
    ? Math.max(system._flaskInterior.minX + radius, bL)
    : bL;
  var spawnMaxX = system._flaskInterior
    ? Math.min(system._flaskInterior.maxX - radius, bR)
    : bR;
  if (spawnMaxX < spawnMinX) {
    var mid = (spawnMinX + spawnMaxX) / 2;
    spawnMinX = mid;
    spawnMaxX = mid;
  }

  var x;
  if (fallMouthCenterX != null && fallMouthWidth != null && fallMouthWidth > 1e-6) {
    var mouthLeft = fallMouthCenterX - fallMouthWidth / 2;
    var t = (screenX - mouthLeft) / fallMouthWidth;
    var clampedT = clamp(t, 0, 1);
    x = spawnMinX + clampedT * (spawnMaxX - spawnMinX);
  } else {
    // Backward-compatible fallback when mouth geometry is unavailable.
    x = centerX + (screenX - fallbackMouthCenterX) * 0.25;
    x = clamp(x, spawnMinX, spawnMaxX);
  }
  var y = system.dropSpawnY;
  var meta = createBodyForSystem(system, tier, x, y);
  if (!meta || !meta.body) return;
  onFlaskBodySpawned(meta);
  World.add(system.world, meta.body);
  system.bodies.push(meta);
  system.bodiesMap[meta.body.id] = meta;
  markFlaskCargoMeta(meta);
  applyFlaskDropLanding(meta, system.bounds);

  var tierColors = C.MERGE_PICKUP_COLORS || [];
  var tierColor = tierColors[tier] || "#94a3b8";
  var count = DROP_PARTICLE_COUNT;
  system.dropSplashStart = Date.now();
  system.dropSplashUntil = system.dropSplashStart + DROP_SPLASH_MS;
  system.dropSplashParticles = createDropParticles(x, y, count, tierColor, system.bounds);

  if (EventBus && EventBus.emit) {
    EventBus.emit("flask:drop", { tier: tier, x: x, y: y });
  }
  requestContainmentBurst(system);
  return meta;
}

export function addBodyAtForSystem(system, tier, x, y, opts) {
  system.ensureEngine();
  var World = Matter.World;
  var meta = createBodyForSystem(system, tier, x, y, opts);
  if (!meta || !meta.body) return null;
  onFlaskBodySpawned(meta, opts);
  World.add(system.world, meta.body);
  system.bodies.push(meta);
  system.bodiesMap[meta.body.id] = meta;
  markFlaskCargoMeta(meta, opts);
  if (opts && opts.dropLanding) applyFlaskDropLanding(meta, system.bounds);
  requestContainmentBurst(system);
  return meta;
}

export function clearBodiesForSystem(system) {
  if (system.bodies.length === 0) return;
  system.ensureEngine();
  var World = Matter.World;
  for (var i = 0; i < system.bodies.length; i++) {
    World.remove(system.world, system.bodies[i].body);
  }
  system.bodies = [];
  system.bodiesMap = {};
}

export function spawnTestBodiesForSystem(system, maxCount) {
  system.ensureEngine();
  var World = Matter.World;
  var chain = system.evolutionChain || [];
  var count = chain.length;
  // Optional cap so callers (e.g. the dev merge boot) can seed just a few low
  // tiers instead of one of every tier, which overflows a normal-size flask.
  if (maxCount != null && maxCount > 0 && maxCount < count) count = maxCount;
  if (count === 0) return;
  var L = system.bounds.left;
  var R = system.bounds.right;
  var T = system.bounds.top;
  var B = system.bounds.bottom;
  var minX = L + 20;
  var maxX = R - 20;
  var minY = T + 60;
  var maxY = B - 20;
  if (system._flaskInterior) {
    var interior = system._flaskInterior;
    minX = interior.minX;
    maxX = interior.maxX;
    minY = Math.max(interior.minY, interior.bodyTop);
    maxY = interior.maxY;
  }
  var bodyW = maxX - minX;
  var bodyH = maxY - minY;
  if (bodyW <= 10 || bodyH <= 10) return;
  var cols = Math.ceil(Math.sqrt(count));
  var rows = Math.ceil(count / cols);
  var cellW = bodyW / (cols + 1);
  var cellH = bodyH / (rows + 1);
  for (var i = 0; i < count; i++) {
    var col = i % cols;
    var row = Math.floor(i / cols);
    var x = minX + (col + 1) * cellW + (Math.random() - 0.5) * cellW * 0.5;
    var y = minY + (row + 1) * cellH + (Math.random() - 0.5) * cellH * 0.5;
    var meta = createBodyForSystem(system, i, x, y);
    onFlaskBodySpawned(meta, { skipOverflowImmunity: true });
    World.add(system.world, meta.body);
    system.bodies.push(meta);
    system.bodiesMap[meta.body.id] = meta;
  }
}